import React, { useState } from "react";
import { Menu, X, Upload } from "lucide-react";
import { Sidebar, MobileSidebarDrawer } from "../../components/layout/AdminSidebar";
import { useNavigate } from "react-router-dom";

interface SlideFormData {
  title: string;
  subtitle: string;
  description: string;
  accent_color: string;
  bg_gradient: string;
  image: File | null;
}

const AddSlidePage: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
const navigate = useNavigate();
  const [formData, setFormData] = useState<SlideFormData>({
    title: "",
    subtitle: "",
    description: "",
    accent_color: "#D4A373",
    bg_gradient: "from-[#D4A373]/20 via-[#F5EBDD]/30 to-[#FAF9F6]",
    image: null,
  });

  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  /* ---------------- INPUT ---------------- */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  /* ---------------- DRAG ---------------- */
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragover" || e.type === "dragenter") setDragActive(true);
    else setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      setFormData((p) => ({ ...p, image: e.dataTransfer.files[0] }));
    }
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setFormData((p) => ({ ...p, image: e.target.files![0] }));
    }
  };

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async () => {
    try {
      setLoading(true);

      if (!formData.title.trim()) throw new Error("Title is required");
      if (!formData.subtitle.trim()) throw new Error("Subtitle is required");
      if (!formData.image) throw new Error("Image is required");

      const fd = new FormData();
      fd.append("title", formData.title);
      fd.append("subtitle", formData.subtitle);
      fd.append("description", formData.description || "");
      fd.append("accent_color", formData.accent_color);
      fd.append("bg_gradient", formData.bg_gradient);
      fd.append("file", formData.image);

      const res = await fetch(`${process.env.PUBLIC_API || 'https://thequran4all.com/api'}/admin/slides`, {
        method: "POST",
        body: fd,
      });

      if (!res.ok) throw new Error("Failed to create slide");

      setMessage({ type: "success", text: "Slide created successfully!" });

      setFormData({
        title: "",
        subtitle: "",
        description: "",
        accent_color: "#D4A373",
        bg_gradient: "from-[#D4A373]/20 via-[#F5EBDD]/30 to-[#FAF9F6]",
        image: null,
      });
      // ✅ navigate after success
        setTimeout(() => {
          navigate("/admin/content_management");
        }, 800);

    } catch (err: any) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500";

  const labelClass = "block text-sm font-semibold text-gray-900 mb-2";

  return (
    <div className="min-h-screen bg-[#F8F9FA]">

      {/* SIDEBAR */}
      <div className="hidden lg:block">
        <Sidebar />
      </div>

      <MobileSidebarDrawer
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />

      {/* MOBILE HEADER */}
      <div className="lg:hidden sticky top-0 z-30 bg-white border-b px-4 py-3 flex items-center gap-3">
        <button onClick={() => setIsSidebarOpen(true)}>
          <Menu />
        </button>
        <h1 className="font-semibold">Add Slide</h1>
      </div>

      {/* MAIN */}
      <div className="lg:ml-64 px-4 py-6 lg:px-8 lg:py-8">

        {/* MESSAGE */}
        {message && (
          <div
            className={`mb-6 p-4 rounded border ${
              message.type === "success"
                ? "bg-green-50 text-green-800 border-green-200"
                : "bg-red-50 text-red-800 border-red-200"
            }`}
          >
            <div className="flex justify-between">
              {message.text}
              <button onClick={() => setMessage(null)}>
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {/* HEADER */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Add New Slide</h1>
          <p className="text-gray-600">Create homepage hero slides</p>
        </div>

        {/* FORM */}
        <div className="bg-white rounded-xl border p-6 space-y-6">

          {/* TITLE */}
          <div>
            <label className={labelClass}>Title *</label>
            <input
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={inputClass}
            />
          </div>

          {/* SUBTITLE */}
          <div>
            <label className={labelClass}>Subtitle *</label>
            <input
              name="subtitle"
              value={formData.subtitle}
              onChange={handleChange}
              className={inputClass}
            />
          </div>

          {/* DESCRIPTION */}
          <div>
            <label className={labelClass}>Description</label>
            <textarea
              name="description"
              rows={4}
              value={formData.description}
              onChange={handleChange}
              className={inputClass}
            />
          </div>

          {/* COLORS */}
          <div className="grid md:grid-cols-2 gap-4">

            <div>
              <label className={labelClass}>Accent Color</label>
              <input
                type="color"
                name="accent_color"
                value={formData.accent_color}
                onChange={handleChange}
                className="w-full h-12 border rounded"
              />
            </div>

        
  {/* IMAGE UPLOAD */}
          <div>
            <label className={labelClass}>Slide Image *</label>

            <div
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-8 text-center ${
                dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300"
              }`}
            >

              <Upload className="mx-auto mb-2 text-gray-500" />

              <p className="text-sm text-gray-600">Drag & drop image</p>

              <input type="file" className="hidden" onChange={handleFile} id="slide" />

              <label htmlFor="slide" className="text-blue-600 cursor-pointer text-sm">
                Browse file
              </label>

              {formData.image && (
                <p className="mt-2 text-green-600 text-sm">
                  ✓ {formData.image.name}
                </p>
              )}

            </div>
          </div>
              <div>
            
            
              <input
              hidden
                name="bg_gradient"
                value={formData.bg_gradient}
                 
                className={inputClass}
              />
            </div>
          </div>

        

          {/* BUTTONS */}
          <div className="flex justify-end gap-3 pt-4 border-t">

            <button
              onClick={() => navigate(-1)}
              className="px-5 py-2 border rounded"
            >
              Cancel
            </button>

            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded"
            >
              {loading ? "Saving..." : "Create Slide"}
            </button>

          </div>

        </div>
      </div>
    </div>
  );
};

export default AddSlidePage;